import { ConfigProvider, Drawer } from 'antd';
import styled from 'styled-components';
import { forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Button, Colors, Row, Text } from 'basic-ui-lib';
import { useGetMedia } from 'modules/playlist/services/hooks/useGetMedia';
import useGetManageLibrary from 'modules/media/services/hooks/useGetManageLibrary';
import { MediaTabContent } from 'modules/dashboard/screens/Detail/components/ContentModal/components';
import { IMediaItem, MediaType } from 'modules/media/interfaces/api';

export type MediaId = string;
export interface ContentMobileModalRef {
  open: (
    currentIds: Set<MediaId>,
    onConfirm?: (result: {
      added: IMediaItem[];
      removed: IMediaItem[];
    }) => void
  ) => void;
}
interface ContentMobileModalProps {}

const ContentMobileModal = forwardRef<ContentMobileModalRef, ContentMobileModalProps>(({}, ref) => {
  const [open, setOpen] = useState(false);
  const [libraryData, fetchLibrary] = useGetManageLibrary();
  const { items, pathStack, openFolder, onBack } = useGetMedia(libraryData, fetchLibrary);
  const [selection, setSelection] = useState<Set<MediaId>>(new Set());
  const initialSelectionRef = useRef<Set<MediaId>>(new Set());
  const onConfirmRef = useRef<
    (result: { added: IMediaItem[]; removed: IMediaItem[] }) => void
  >(() => {});

  const currentFolder = pathStack[pathStack.length - 1] ?? { label: '', path: '' };

  useImperativeHandle(ref, () => ({
    open: (currentIds, onConfirm) => {
      setOpen(true);
      if (currentIds) {
        setSelection(new Set(currentIds));
      } else {
        setSelection(new Set());
      }
      initialSelectionRef.current = new Set(currentIds);
      onConfirmRef.current = onConfirm || (() => undefined);
    },
  }));

  const handleClose = () => {
    setOpen(false);
  };

  const handleOk = () => {
    const initial = initialSelectionRef.current;
    const current = selection;
    const addedIds = [...current].filter((id) => !initial.has(id));
    const removedIds = [...initial].filter((id) => !current.has(id));

    const map = new Map(items.map((i) => [i.id, i]));

    const added = addedIds.map((id) => map.get(id)).filter(Boolean) as IMediaItem[];
    const removed = removedIds.map((id) => map.get(id)).filter(Boolean) as IMediaItem[];

    onConfirmRef.current({ added, removed });

    setOpen(false);
  };

  const selectedMediaIds = useMemo(() => Array.from(selection), [selection]);

  const handleMediaItemClick = useCallback(
    (item: IMediaItem) => {
      if (item.type === MediaType.FOLDER) {
        openFolder(item);
        return;
      }

      setSelection((prev) => {
        const next = new Set(prev);
        next.has(item.id) ? next.delete(item.id) : next.add(item.id);
        return next;
    });
    },
    [openFolder],
  );

  const handleBackClick = () => {
    onBack();
  };

  return (
    <ConfigProvider>
      <StyledDrawer
        title={
          <DrawerHeader>
            <Text title_h9 semibold center>
              Content
            </Text>
          </DrawerHeader>
        }
        placement='bottom'
        closable={false}
        onClose={handleClose}
        open={open}
        height='90%'
        footer={
          <Row>
            <Button type_cancel lg onClick={handleClose} style={{ width: '100%' }}>
              Close
            </Button>
            <Button type_second lg style={{ width: '100%' }} onClick={handleOk}>
              OK
            </Button>
          </Row>
        }
        destroyOnClose
        style={{ borderRadius: '10px 10px 0px 0px' }}
      >
        <MediaTabContent
          items={items}
          currentPath={currentFolder.path}
          folderName={currentFolder.label}
          selectedMediaIds={selectedMediaIds}
          onMediaItemClick={handleMediaItemClick}
          onBackClick={handleBackClick}
        />
      </StyledDrawer>
    </ConfigProvider>
  );
});

ContentMobileModal.displayName = 'ContentMobileModal';
export default ContentMobileModal;

const StyledDrawer = styled(Drawer)`
  .ant-drawer-body {
    padding: 16px 16px 32px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    background-color: ${Colors.neutral.bg[2]};
  }

  .ant-drawer-footer {
    padding: 16px 16px;
    box-shadow: 0px -4px 8px 0px #0000001a;
    background-color: ${Colors.neutral.bg[1]};
  }

  .ant-drawer-header {
    padding: 12px 16px;
  }
`;

const DrawerHeader = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;
